import {Injectable} from '@angular/core';
import {ReportApi} from '@webmate-web-core/shared/api/rest/report.restapi';
import {EventsService} from '@webmate-web-core/shared/services/events/events.service';
import {
    ReportInfo,
    ReportOutputType,
    ReportSharingToken,
    ReportTemplate, ReportTemplateType,
    ReportUpdateAction
} from '../../../report/types';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {BlobId, ProjectId} from '@webmate-web-core/shared/types/ids';
import {isUUID} from '@webmate-web-core/shared/utils/utils';
import {filter, take, tap} from 'rxjs/operators';
import {DateTime} from '@webmate-web-core/shared/types/base-types';
import {GenericApiResponse} from '@webmate-web-core/shared/api/apibase.api';
import {Report, ReportId, ReportOutput} from '../../../report/report_types';
import {withResultCache} from '@webmate-web-core/shared/utils/cache-util';

/**
 * Provides access to the report domain in webmate.
 */
@Injectable({
    providedIn: 'root'
})
export class ReportService {

    private reportCache: { [s: ReportId]: ReplaySubject<ReportInfo> } = {};

    private _reportEventsHandleByProject: { [s: ProjectId]: Observable<any> } = {}; //events

    private eventForwardingSubjects: { [s: ProjectId]: Subject<any> } = {}; //events

    private _registerEventsForProject(projectId: ProjectId): void {
        if (!this.eventForwardingSubjects[projectId]) {
            this.eventForwardingSubjects[projectId] = new Subject<any>();
        }
        if (isUUID(projectId) && this._reportEventsHandleByProject[projectId] === undefined) {
            this._reportEventsHandleByProject[projectId] = this.eventService.getEventsForProject(projectId);
            this._reportEventsHandleByProject[projectId].pipe(filter(ev => ev.eventType.startsWith("Report."))).subscribe(
                next => this._handleEvents(next, projectId)
            );
        }
    }

    constructor(private api: ReportApi, private eventService: EventsService) {}

    private _handleEvents(e: any, forProject: ProjectId): void {
        if (e === undefined) return;
        if (!!this.eventForwardingSubjects[forProject]) {
            this.eventForwardingSubjects[forProject].next(e);
        }
        if (e.eventType.startsWith("Report.Report.")) {
            if (!!this.reportCache[e['reportId']]) {
                this.api.getReportInfo(e['reportId']).pipe(take(1)).subscribe(r => {
                    if (!this.reportCache[r.id]) this.reportCache[r.id] = new ReplaySubject<ReportInfo>(1);
                        this.reportCache[r.id].next(r)
                    });
            }
        }
    }

    getReport$(id: ReportId): Observable<Report> {
        return this.api.getReport(id);
    }

    registerEventsForProject(projectId: ProjectId): Observable<any> {
        this._registerEventsForProject(projectId);
        return this.eventForwardingSubjects[projectId];
    }

    getBlobUrl(reportId: ReportId, blobId: BlobId, asAttachment?: boolean, attachmentName?: string): string {
        return this.api.getBlobUrl(reportId, blobId, asAttachment, attachmentName);
    }

    getReportInfo$(id: ReportId): Observable<ReportInfo> {
        return withResultCache(() => this.api.getReportInfo(id), this.reportCache, id);
    }

    getReportInfosForProject$(projectId: ProjectId): Observable<ReportInfo[]> {
        return this.api.getReportInfosForProject(projectId);
    }


    getReportContentBlob$(id: ReportId, blobId: BlobId): Observable<Blob> {
        return this.api.getReportContentBlob(id, blobId);
    }

    updateReport(id: ReportId, update: ReportUpdateAction) {
        return this.api.updateReport(id, [update]);
    }

    deleteReport(id: ReportId) {
        return this.api.deleteReport(id).pipe(
            tap(() => {
              delete this.reportCache[id];
            })
          );
    }

    publishReport(id: ReportId) {
        return this.api.publishReport(id);
    }

    getReportSharingTokens$(id: ReportId): Observable<ReportSharingToken[]> {
        return this.api.getReportSharingTokens(id);
    }

    createNewSharingToken$(id: ReportId, name: string, expirationTime?: DateTime): Observable<ReportSharingToken> {
        return this.api.createSharingToken(id, name, expirationTime);
    }

    deleteSharingToken$(reportId: ReportId, tokenId: string): Observable<GenericApiResponse> {
        return this.api.deleteSharingToken(reportId, tokenId);
    }

    generateOutput(reportId: ReportId, outputType: ReportOutputType): Observable<ReportOutput> {
        return this.api.generateOutput(reportId, outputType);
    }

    getReportPreviewUrl$(reportId: ReportId): Observable<string> {
        return this.api.getReportPreviewUrl(reportId);
    }

    getReportTemplatesForProject$(projectId: ProjectId, templateType?: ReportTemplateType, includeBaseProject: boolean = true): Observable<ReportTemplate[]> {
        return this.api.getReportTemplatesForProject(projectId, templateType, includeBaseProject);
    }

    createReportTemplate$(projectId: ProjectId, template: Omit<ReportTemplate, 'id' | 'isBaseTemplate'>): Observable<ReportTemplate> {
        return this.api.createReportTemplate(projectId, template);
    }

    updateReportTemplate$(projectId: ProjectId, templateId: string, template: Partial<ReportTemplate>): Observable<ReportTemplate> {
        return this.api.updateReportTemplate(projectId, templateId, template);
    }

    deleteReportTemplate$(projectId: ProjectId, templateId: string): Observable<GenericApiResponse> {
        return this.api.deleteReportTemplate(projectId, templateId);
    }
}

